/* SELEZIONE DELLE COLONNE.
 *
 * Un gruppo è una griglia di parametri: le metriche sono tante e su 390px non
 * ci stanno. La scelta serve a questo, e vale sia per la tabella del desktop
 * sia per le schede del telefono — se valesse solo per la tabella, la
 * larghezza in cui il problema esiste davvero sarebbe l'unica non servita.
 *
 * Quello che NON si può togliere: profitto, drawdown e operazioni. Non sono
 * tre colonne, sono una terna (regola 1). Il prototipo lasciava spegnere le
 * operazioni; qui no, perché una riga con profitto e drawdown soli è
 * esattamente la mezza verità che la regola vieta. Le tre compaiono comunque
 * nell'elenco, bloccate e col motivo scritto: una colonna che non si vede e
 * non si spiega si legge come un pezzo mancante.
 *
 * La scelta resta in `localStorage`: rifarla a ogni visita sarebbe una tassa
 * su chi lavora sempre sulle stesse metriche. Nessun endpoint: le preferenze
 * di visualizzazione non esistono lato backend.
 */

import { useCallback, useEffect, useState } from 'react';

export type ChiaveColonna =
  'tenuta' | 'ribassi' | 'intorno' | 'pariRischio' | 'calmar' | 'sharpe' | 'cagr';

export interface DefinizioneColonna {
  chiave: ChiaveColonna;
  etichetta: string;
  /** Cosa dice davvero la colonna. Visibile nell'elenco, non in un tooltip. */
  nota: string;
}

export const COLONNE: DefinizioneColonna[] = [
  { chiave: 'tenuta', etichetta: 'Tenuta', nota: 'rende ancora come rendeva' },
  {
    chiave: 'ribassi',
    etichetta: 'Nei ribassi',
    nota: 'cosa fa negli anni in cui il mercato perde',
  },
  {
    chiave: 'intorno',
    etichetta: 'Intorno',
    nota: 'quanto resta se un parametro sbaglia di una tacca',
  },
  { chiave: 'pariRischio', etichetta: 'A pari rischio', nota: 'profitto riportato a drawdown 35%' },
  { chiave: 'calmar', etichetta: 'Calmar', nota: 'rendimento annuo diviso drawdown' },
  { chiave: 'sharpe', etichetta: 'Sharpe', nota: 'rendimento diviso volatilità' },
  { chiave: 'cagr', etichetta: 'CAGR', nota: 'rendimento annualizzato' },
];

/** Le tre che non si spengono, con il motivo accanto. */
export const COLONNE_FISSE = [
  { etichetta: 'Profitto', nota: 'la terna non si spezza' },
  { etichetta: 'Drawdown', nota: 'la terna non si spezza' },
  { etichetta: 'Operazioni', nota: 'la terna non si spezza' },
];

export type SceltaColonne = Record<ChiaveColonna, boolean>;

/* `intorno` è accesa di serie: in una griglia di parametri è la colonna che
   distingue un altopiano da un picco, ed è il motivo per cui il gruppo esiste.
   Sharpe e CAGR restano spente perché ripetono in altra forma quello che
   profitto e drawdown già dicono. */
export const SCELTA_PREDEFINITA: SceltaColonne = {
  tenuta: true,
  ribassi: true,
  intorno: true,
  pariRischio: true,
  calmar: true,
  sharpe: false,
  cagr: false,
};

const CHIAVE_ARCHIVIO = 'sb.v2.colonne-gruppo';

function leggiArchivio(): SceltaColonne {
  try {
    const grezzo = localStorage.getItem(CHIAVE_ARCHIVIO);
    if (!grezzo) return SCELTA_PREDEFINITA;
    const salvato = JSON.parse(grezzo) as Partial<SceltaColonne>;
    /* Si riparte sempre dal predefinito e si sovrascrive: così una colonna
       aggiunta domani compare, invece di restare invisibile a chi ha già una
       scelta salvata senza quella chiave. */
    const unione = { ...SCELTA_PREDEFINITA };
    for (const c of COLONNE) {
      if (typeof salvato[c.chiave] === 'boolean') unione[c.chiave] = salvato[c.chiave] as boolean;
    }
    return unione;
  } catch {
    return SCELTA_PREDEFINITA;
  }
}

export function usaSceltaColonne() {
  const [scelta, setScelta] = useState<SceltaColonne>(leggiArchivio);

  useEffect(() => {
    try {
      localStorage.setItem(CHIAVE_ARCHIVIO, JSON.stringify(scelta));
    } catch {
      /* Archivio pieno o negato: la scelta vale comunque per questa visita. */
    }
  }, [scelta]);

  const inverti = useCallback((chiave: ChiaveColonna) => {
    setScelta((s) => ({ ...s, [chiave]: !s[chiave] }));
  }, []);

  const azzera = useCallback(() => setScelta(SCELTA_PREDEFINITA), []);

  return { scelta, inverti, azzera };
}

/** Quante colonne opzionali sono accese: serve a scriverlo sul pulsante. */
export function quanteAccese(scelta: SceltaColonne): number {
  return COLONNE.filter((c) => scelta[c.chiave]).length;
}
